import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { useMutation, useQuery } from '@apollo/client';

import { breakpoints } from '@notino/react-styleguide';
import {
  GetCombinedAdsProductsQuery,
  GetCombinedAdsProductsQueryVariables,
  ReportSponsoredAdsProductClickMutation,
  ReportSponsoredAdsProductClickMutationVariables,
  ReportSponsoredAdsProductImpressionMutation,
  ReportSponsoredAdsProductImpressionMutationVariables,
  SponsoredAdsPlacement,
} from '@notino/shared/definitions/types';

import { useProductSectionsStateInfo } from '@containers/ProductDetailContainer/useProductSectionsStateInfo';
import { EROTICS_NAVIGATION_VALUES } from '@lib/constants';
import { styled } from '@linaria/react';

import messages from '../../messages';
import { useProductDetailContext } from '../../ProductDetailContext';
import { useLastVisitedItems } from '../LastVisitedProducts/useLastVisitedItems';
import { BrandsId } from '../model';
import { ProductsSection } from '../ProductsSection';

import { LIST_LABEL, MAX_SPONSORED_PRODUCTS } from './constants';
import getCombinedAdsQuery from './queries/combinedAds.graphql';
import reportClickMutation from './queries/reportSponsoredAdsProductClick.graphql';
import reportImpressionMutation from './queries/reportSponsoredAdsProductImpression.graphql';

const Wrap = styled.div`
  margin-bottom: 2rem;
  @media (min-width: ${breakpoints.lg}) {
    margin-bottom: 4rem;
  }
`;

export const SponsoredAdsProducts: React.FC = React.memo(() => {
  const [ref, inView] = useInView({ triggerOnce: true });
  const { product, currentVariant } = useProductDetailContext();
  const lastVisitedIds = useLastVisitedItems(product.webMasterId).map(Number);
  const { displayedRelatedProductIds, isRoutineDisplayed } =
    useProductSectionsStateInfo();

  const isErotics = currentVariant.navigationValues?.some((navValue) =>
    EROTICS_NAVIGATION_VALUES.includes(navValue)
  );
  const excludedCategories = isErotics
    ? [Number(BrandsId.DIOR)]
    : [Number(BrandsId.DIOR), ...EROTICS_NAVIGATION_VALUES];

  const { data, loading } = useQuery<
    GetCombinedAdsProductsQuery,
    GetCombinedAdsProductsQueryVariables
  >(getCombinedAdsQuery, {
    variables: {
      input: {
        sponsoredAds: [
          {
            placement: SponsoredAdsPlacement.Product,
            requestIdentifier: 'pd-sponsored-products',
            excludedProducts: [
              ...lastVisitedIds,
              ...displayedRelatedProductIds,
            ],
            excludedCategories,
            sponsoredProducts: {
              count: MAX_SPONSORED_PRODUCTS,
              productId: Number(product.webMasterId),
            },
          },
        ],
      },
    },
    skip: !inView || isRoutineDisplayed,
    ssr: false,
  });

  const sponsoredProducts = React.useMemo(
    () =>
      data?.CombinedAds?.sponsoredAds?.flatMap(
        (ad) => ad.sponsoredProducts ?? []
      ) ?? [],
    [data]
  );

  const sponsoredProductIds = sponsoredProducts.map((p) => String(p.productId));

  const campaignIdByProductId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const p of sponsoredProducts) {
      if (p.campaignId) {
        map.set(String(p.productId), p.campaignId);
      }
    }
    return map;
  }, [sponsoredProducts]);

  const [reportClick] = useMutation<
    ReportSponsoredAdsProductClickMutation,
    ReportSponsoredAdsProductClickMutationVariables
  >(reportClickMutation);

  const [reportImpression] = useMutation<
    ReportSponsoredAdsProductImpressionMutation,
    ReportSponsoredAdsProductImpressionMutationVariables
  >(reportImpressionMutation);

  const pageUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : '';

  const handleProductClick = React.useCallback(
    (productId: string) => {
      const campaignId = campaignIdByProductId.get(productId);
      if (campaignId) {
        void reportClick({ variables: { url: pageUrl, campaignId } });
      }
    },
    [campaignIdByProductId, reportClick, pageUrl]
  );

  const handleProductImpression = React.useCallback(
    (productId: string) => {
      const campaignId = campaignIdByProductId.get(productId);
      if (campaignId) {
        void reportImpression({ variables: { url: pageUrl, campaignId } });
      }
    },
    [campaignIdByProductId, reportImpression, pageUrl]
  );

  if (inView && !loading && sponsoredProductIds.length < 3) {
    return null;
  }

  return (
    <div ref={ref} data-testid="sponsored-ads-products-wrapper">
      {inView && (
        <Wrap>
          <ProductsSection
            productIds={sponsoredProductIds.slice(0, MAX_SPONSORED_PRODUCTS)}
            productIdsLoading={loading}
            titleMessage={messages.sponsoredProducts}
            titleId="pdSponsoredAdsProducts"
            containerId="pd-sponsored-ads-products-header"
            listLabel={LIST_LABEL}
            withDivider={false}
            trackProductClick={true}
            trackProductImpression={true}
            isSponsored={true}
            onProductClick={handleProductClick}
            onProductImpression={handleProductImpression}
          />
        </Wrap>
      )}
    </div>
  );
});
